import { includes } from 'ramda';
import { useCallback, useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from '../hooks';
import { shortcutKeysAtom } from '../recoil';
import { preventDefaultAndPropagation } from '../utils';
/**
 * useKeyboardShortcut is used to handle actions such as saving data,
 * downloading documents, or opening and closing modals using keyboard
 * shortcuts. It also utilizes the shortcutKeysAtom to keep track of
 * which shortcuts are available in the shortcut legend.
 *
 * @param dependecy expresses the condition under which shortcut is allowed
 * @param key key to press in combination with Ctrl in order to trigger action
 * @param translationPrefix a translationPrefix of the action triggered by the shortcut
 * @param callback a callback which triggers an action on keydown
 * @param options an object for additional hook options
 * @returns string for attribute aria-keyboardshortcuts
 */
export function useKeyboardShortcut({ key, callback, translationPrefix, dependency, options, }) {
    const { t } = useTranslation();
    const setShortcutTranslationKeys = useSetRecoilState(shortcutKeysAtom);
    const onKeydown = useCallback((e) => {
        const shortcut = e.key === key && e.ctrlKey;
        if (shortcut && dependency && !options?.isModalOpen) {
            preventDefaultAndPropagation(e);
            callback();
        }
        else if (shortcut) {
            preventDefaultAndPropagation(e);
            if (options?.isModalOpen) {
                callback();
            }
        }
    }, [callback, dependency, key, options?.isModalOpen]);
    useEffect(() => {
        document.addEventListener('keydown', onKeydown);
        return () => {
            document.removeEventListener('keydown', onKeydown);
        };
    }, [onKeydown]);
    const handleTranslationPrefixs = useCallback(() => {
        if (dependency) {
            setShortcutTranslationKeys((prev) => includes({ translationPrefix, key }, prev)
                ? prev
                : [{ translationPrefix, key }, ...prev]);
        }
    }, [dependency, translationPrefix, key, setShortcutTranslationKeys]);
    const handleCleanup = useCallback(() => setShortcutTranslationKeys((prev) => includes({ translationPrefix, key }, prev)
        ? prev.filter((item) => item.key !== key)
        : prev), [translationPrefix, key, setShortcutTranslationKeys]);
    useEffect(() => {
        handleTranslationPrefixs();
        return () => {
            handleCleanup();
        };
    }, [handleCleanup, handleTranslationPrefixs]);
    const ariaKeyShortcuts = t('shortcuts.aria', { command: key });
    return ariaKeyShortcuts;
}
